<template>
  <Modal
    :is-open="isOpen"
    icon="ballot-check"
    :title="$t('ui.common.contactPreference', 2)"
    description="Update the contact preferences for this customer."
    :buttons="['ok', 'close']"
    close-text="Cancel"
    ok-text="Save"
    min-width="400"
    max-width="400"
    @close-modal="$emit('close')"
    @ok-modal="handleSave"
  >
    <ListGroup>
      <ListGroupItem>
        <Checkbox
          v-model="localContactPermissions.canCall"
          no-margin
          label="Can Call"
          description="Outbound calls"
        />
      </ListGroupItem>
      <ListGroupItem>
        <Checkbox
          v-model="localContactPermissions.canEmail"
          no-margin
          label="Can Email"
          description="Manual and automatic"
        />
      </ListGroupItem>
      <ListGroupItem>
        <Checkbox
          v-model="localContactPermissions.canSms"
          no-margin
          label="Can SMS"
          description="Manual and automatic"
        />
      </ListGroupItem>
    </ListGroup>
    <div class="mt-6 text-sm text-secondary">
      By saving these changes, I confirm that I have the customer's consent to contact them using the selected methods.
    </div>
  </Modal>
</template>

<script setup lang="ts">
// composables
import { reactive, watch } from 'vue'
// components
import Modal from '@/components/modal/Modal.vue'
import ListGroup from '@/components/list/ListGroup.vue'
import ListGroupItem from '@/components/list/ListGroupItem.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
// types
import type { AgreementContactPermissions } from '@/types/domain/agreement'

interface Props {
  isOpen: boolean
  contactPermissions?: AgreementContactPermissions | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  save: [permissions: { canCall: boolean; canEmail: boolean; canSms: boolean }]
}>()

const localContactPermissions = reactive({
  canCall: props.contactPermissions?.canCall ?? false,
  canEmail: props.contactPermissions?.canEmail ?? false,
  canSms: props.contactPermissions?.canSms ?? false
})

watch(
  () => props.contactPermissions,
  newPermissions => {
    if (newPermissions) {
      localContactPermissions.canCall = newPermissions.canCall
      localContactPermissions.canEmail = newPermissions.canEmail
      localContactPermissions.canSms = newPermissions.canSms
    }
  },
  { deep: true }
)

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen && props.contactPermissions) {
      localContactPermissions.canCall = props.contactPermissions.canCall
      localContactPermissions.canEmail = props.contactPermissions.canEmail
      localContactPermissions.canSms = props.contactPermissions.canSms
    }
  }
)

function handleSave() {
  emit('save', { ...localContactPermissions })
}
</script>
